/** Single-click vs double-click disambiguation for Media Cards
 * (VISTOERA_CONTENT_CANVAS_MEDIA_CARD_AND_GENERATION_INTERACTION_V2
 * §5/§13/§46 — "SINGLE CLICK -> Inspector, DOUBLE CLICK -> Preview,
 * 必须区分"). The browser fires `click` (twice) THEN `dblclick` on a
 * real double-click, so a naive `onClick` handler always runs its
 * single-click side effect first — this delays the single-click action
 * just long enough (`delayMs`) for a following `dblclick` to arrive and
 * cancel it outright, exactly the "priority: DOUBLE CLICK MEDIA ->
 * Preview" ordering spec §46 requires. */

import { useCallback, useEffect, useRef } from 'react'

export function useSingleOrDoubleClick(onSingleClick: () => void, onDoubleClick: () => void, delayMs = 220) {
  const timerRef = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current)
    },
    [],
  )

  const handleClick = useCallback(() => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null
      onSingleClick()
    }, delayMs)
  }, [delayMs, onSingleClick])

  const handleDoubleClick = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    onDoubleClick()
  }, [onDoubleClick])

  return { onClick: handleClick, onDoubleClick: handleDoubleClick }
}
